import * as React from 'react';
import * as _ from 'lodash';

import {Button, Checkbox, Dropdown, DropdownItemProps, Icon} from 'semantic-ui-react';
import {EColumnKey} from "../../../../views/OptionsView/OptionsViewUtils";
import {getPCCDFilterLabel, getPDiPFilterLabel, PCCDFlag, PDiPFlag} from "../../VirtualizedTableUtils";
import {PCCDFilterEnum, PDiPFilterEnum} from "../../VirtualizedTableEnums";
import {IVirtualizedTableColumn, IVisibleTableColumnList, TDerivateColumnSettings} from "../../VirtualizedTableInterfaces";
import Tooltip from "../../../Tooltip";
import {IPslSelection} from 'src/views/OptionsView/PSL/PslSelect';
import {setTimeout} from 'timers';
import TableFooterLoader from './TableFooterLoader';

const SELECT_ALL_KEY = "__selectAll__";

interface TableFooterActionsProps {
    t: reactI18Next.TranslationFunction;
    visibleColumns: IVisibleTableColumnList;
    onChange: (
        rowVisibleList: IVisibleTableColumnList,
        resetHideEmptyRowsAndColumnsFilter?: boolean,
        key?: string,
        clearHeaderFilters?: boolean
    ) => void;
    derivatesList?: Map<number, string>;
    availableDerivates?: number[];
    onChangeViewMode?: () => void;
    // true for full view mode, false for normal mode
    isFullViewMode?: boolean;
    loadedPSL?: IPslSelection;
    pdipFilterValue?: PDiPFilterEnum;
    pccdFilterValue?: PCCDFilterEnum;
    onFilterChange?: (key: string, val: string) => void;
    columnLabels: IVirtualizedTableColumn[];
    isSpecificMarket?: boolean;

    // filter by comparing assignment types
    visibleDerivates?: string[];
    emptyDerivates?: number[];
    compareAssignmentsFilterValue?: Set<string>;
    onFilterByComparingAssignments?: (value: Set<string>) => void;

    // flag and function used to hide options with no assignments and derivates that are not used
    hideEmptyAssignmentsRowsAndColumns?: boolean;
    onHideEmptyAssignmentsRowsAndColumns?: () => void;
    generationsRows?: Array<any>;
    isNewGridTable?: boolean;
}

export default class TableFooterActions extends React.Component<TableFooterActionsProps> {
    state = {
        isDerivateChanging: false,
        changingColumnKey: null as string | null,
        changingDerivateKey: null as number | string | null,
    };

    componentDidUpdate(prevProps: Readonly<TableFooterActionsProps>) {

        if (this.props.hideEmptyAssignmentsRowsAndColumns !== undefined
            && this.props.onHideEmptyAssignmentsRowsAndColumns
            && this.props.emptyDerivates
        ) {
            const toggled = this.props.hideEmptyAssignmentsRowsAndColumns;
            const prevToggled = prevProps.hideEmptyAssignmentsRowsAndColumns;
            // if the button went from OFF to ON, then filter out the empty derivates
            if (toggled && !prevToggled && this.props.emptyDerivates.length > 0) {
                this.toggleEmptyDerivates(this.props.emptyDerivates);
            }

            // if the button went from ON to OFF, show all derivates
            if (!toggled && prevToggled) {
                this.toggleEmptyDerivates([]);
            }
        }
    }

    onChange(key: string, visible: boolean) {
        this.setState({isDerivateChanging: true, changingColumnKey: key});
        setTimeout(
            () => {
                const newList = _.cloneDeep(this.props.visibleColumns);
                newList[key].visible = visible;
                this.props.onChange(newList, undefined, key);
            },
            0
        );

        setTimeout(
            () => {
                this.setState({isDerivateChanging: false, changingColumnKey: null});
            },
            1000
        ); // 1 second, or replace with actual callback if available
    }

    onChangeDerivate(key: string, derivates: number[], derivateKey: string) {
        this.setState({isDerivateChanging: true, changingDerivateKey: derivateKey});
        setTimeout(
            () => {
                if (this.props.loadedPSL) {
                    const newList = _.cloneDeep(this.props.visibleColumns);
                    let hiddenDerivates = newList[key].hiddenDerivates !== undefined ? newList[key].hiddenDerivates : [];

                    if (!hiddenDerivates || hiddenDerivates.length === 0) {
                        const item: TDerivateColumnSettings = {
                            pslSelection: this.props.loadedPSL,
                            derivates
                        };
                        newList[key].hiddenDerivates = [item];
                    } else {
                        const index = hiddenDerivates.findIndex((item) => _.isEqual(item.pslSelection, this.props.loadedPSL));
                        if (index > -1) {
                            let setting = hiddenDerivates[index];
                            if (setting.derivates.find((der) => derivates.includes(der))) {
                                setting.derivates = setting.derivates.filter((der) => !derivates.includes(der));
                            } else {
                                setting.derivates = setting.derivates.concat(derivates);
                            }
                            if (setting.derivates.length === 0) {
                                newList[key].hiddenDerivates = hiddenDerivates.filter(item => !_.isEqual(item.pslSelection, this.props.loadedPSL));
                            } else {
                                hiddenDerivates[index] = setting;
                                newList[key].hiddenDerivates = hiddenDerivates;
                            }
                        } else {
                            const item: TDerivateColumnSettings = {
                                pslSelection: this.props.loadedPSL,
                                derivates
                            };
                            newList[key].hiddenDerivates = hiddenDerivates.concat(item);
                        }
                    }
                    this.props.onChange(newList, true);
                }
            },
            0
        );

        setTimeout(
            () => {
                this.setState({isDerivateChanging: false, changingDerivateKey: null});
            },
            500
        );
    }

    toggleEmptyDerivates = (emptyDerivates: number[]) => {
        if (this.props.loadedPSL) {
            const newList = _.cloneDeep(this.props.visibleColumns);
            const hiddenDerivates = newList[EColumnKey.DerivateKey].hiddenDerivates !== undefined
                ? newList[EColumnKey.DerivateKey].hiddenDerivates
                : [];

            // @ts-ignore it cannot be undefined
            const index = hiddenDerivates.findIndex((item) => _.isEqual(item.pslSelection, this.props.loadedPSL));

            if (index > -1) {
                // @ts-ignore it cannot be undefined
                let setting = hiddenDerivates[index];

                setting.derivates = emptyDerivates.length > 0 ? [...setting.derivates, ...emptyDerivates] : [];

                // @ts-ignore it cannot be undefined
                hiddenDerivates[index] = setting;
                newList[EColumnKey.DerivateKey].hiddenDerivates = hiddenDerivates;
            }
            // if the setting for the loaded PSL does not exists => create it
            else {
                const setting: TDerivateColumnSettings = {
                    pslSelection: this.props.loadedPSL,
                    derivates: emptyDerivates,
                };
                // @ts-ignore it cannot be undefined
                newList[EColumnKey.DerivateKey].hiddenDerivates = hiddenDerivates.concat(setting);
            }

            this.props.onChange(newList);
        }
    }

    selectAllColumns(checked: boolean) {
        this.setState({isDerivateChanging: true, changingColumnKey: SELECT_ALL_KEY});
        setTimeout(
            () => {
                let newList = {...this.props.visibleColumns};
                Object.keys(this.props.visibleColumns).forEach(key => {
                    if (key !== EColumnKey.DerivateKey) {
                        newList[key].visible = checked;
                    }
                });
                this.props.onChange(newList, undefined, undefined, !checked);
            },
            0
        );
        setTimeout(
            () => {
                this.setState({isDerivateChanging: false, changingColumnKey: null});
            },
            500
        );
    }

    selectAllDerivates(checked: boolean) {
        this.setState({isDerivateChanging: true, changingDerivateKey: SELECT_ALL_KEY});
        setTimeout(
            () => {
                if (this.props.loadedPSL && this.props.derivatesList) {
                    const newList = _.cloneDeep(this.props.visibleColumns);
                    let hiddenDerivates: TDerivateColumnSettings[] | undefined = newList[EColumnKey.DerivateKey].hiddenDerivates;

                    if (checked) {
                        // Hide all
                        if (hiddenDerivates && hiddenDerivates.length > 0) {
                            const index = hiddenDerivates.findIndex((item) => _.isEqual(item.pslSelection, this.props.loadedPSL));
                            if (index > -1) {
                                hiddenDerivates[index].derivates = Array.from(this.props.derivatesList.keys());
                                newList[EColumnKey.DerivateKey].hiddenDerivates = hiddenDerivates;
                            } else {
                                const item: TDerivateColumnSettings = {
                                    pslSelection: this.props.loadedPSL,
                                    derivates: Array.from(this.props.derivatesList.keys())
                                };
                                newList[EColumnKey.DerivateKey].hiddenDerivates = hiddenDerivates.concat(item);
                            }
                        } else {
                            const item: TDerivateColumnSettings = {
                                pslSelection: this.props.loadedPSL,
                                derivates: Array.from(this.props.derivatesList.keys())
                            };
                            newList[EColumnKey.DerivateKey].hiddenDerivates = [item];
                        }
                    } else {
                        // Show all
                        if (hiddenDerivates) {
                            newList[EColumnKey.DerivateKey].hiddenDerivates = hiddenDerivates.filter(item => !_.isEqual(item.pslSelection, this.props.loadedPSL));
                        }
                    }
                    this.props.onChange(newList, true);
                }
            },
            0
        );
        setTimeout(
            () => {
                this.setState({isDerivateChanging: false, changingDerivateKey: null});
            },
            500
        );
    }

    private getPDiPFilterClass = (value: string) => {
        let className = "icon";
        switch (value) {
            case (PDiPFilterEnum.SHOW_PDIP_INVALID):
                className = className + " pdip-filter-invalid";
                break;
            case (PDiPFilterEnum.SHOW_PDIP_VALID):
                className = className + " pdip-filter-valid";
                break;
            default:
                break;
        }

        return className;
    }

    private renderPDiPFilterLabel = (item: DropdownItemProps) => ({
        content: item.text,
        className: item.className
    })

    private getPDiPFilterDataSource = () => ([
        {
            text: getPDiPFilterLabel(PDiPFilterEnum.SHOW_ALL),
            value: PDiPFilterEnum.SHOW_ALL,
            className: "font-weight--bold"
        },
        {
            text: getPDiPFilterLabel(PDiPFilterEnum.SHOW_PDIP_VALID),
            value: PDiPFilterEnum.SHOW_PDIP_VALID,
            className: "font-weight--bold"
        },
        {
            text: getPDiPFilterLabel(PDiPFilterEnum.SHOW_PDIP_INVALID),
            value: PDiPFilterEnum.SHOW_PDIP_INVALID,
            className: "font-weight--bold color--pdipInvalidColor"
        }
    ])

    private getPCCDFilterClass = (value: string) => {
        let className = "icon";
        switch (value) {
            case (PCCDFilterEnum.SHOW_PCCD_INVALID):
                className = className + " pccd-filter-invalid";
                break;
            case (PCCDFilterEnum.SHOW_PCCD_VALID):
                className = className + " pccd-filter-valid";
                break;
            default:
                break;
        }

        return className;
    }

    private renderPCCDFilterLabel = (item: DropdownItemProps) => ({
        content: item.text,
        className: item.className
    })

    private getPCCDFilterDataSource = () => ([
        {
            text: getPCCDFilterLabel(PCCDFilterEnum.SHOW_ALL),
            value: PCCDFilterEnum.SHOW_ALL,
            className: "font-weight--bold"
        },
        {
            text: getPCCDFilterLabel(PCCDFilterEnum.SHOW_PCCD_VALID),
            value: PCCDFilterEnum.SHOW_PCCD_VALID,
            className: "font-weight--bold"
        },
        {
            text: getPCCDFilterLabel(PCCDFilterEnum.SHOW_PCCD_INVALID),
            value: PCCDFilterEnum.SHOW_PCCD_INVALID,
            className: "font-weight--bold color--pccdInvalidColor"
        }
    ])

    private getLabel = (key: string) => {
        const result = this.props.columnLabels.find((item: IVirtualizedTableColumn) => item.key === key);
        return result ? result.label : "";
    }

    onFilterByComparingAssignments(derivate: string) {
        if (this.props.onFilterByComparingAssignments) {
            let newFilter = this.props.compareAssignmentsFilterValue ? new Set<string>(this.props.compareAssignmentsFilterValue) : new Set<string>();
            if (newFilter.has(derivate)) {
                newFilter.delete(derivate);
            } else {
                newFilter.add(derivate);
            }

            this.props.onFilterByComparingAssignments(newFilter);
        }
    }
    private onCheckDerivate = (generation: any) => {

        if (this.props.visibleColumns[EColumnKey.DerivateKey] && this.props.visibleColumns[EColumnKey.DerivateKey].hiddenDerivates !== undefined) {
            // @ts-ignore
            const derivates = this.props.visibleColumns[EColumnKey.DerivateKey].hiddenDerivates;
            // @ts-ignore
            const setting = derivates.find((item) => _.isEqual(item.pslSelection, this.props.loadedPSL));
            if (setting && generation.child.length === 0) {
                return !(setting.derivates.includes(generation.objectId));
            } else if (setting) {
                const childObjectId = generation.child.map((child: any) => child.objectId);
                childObjectId.push(generation.objectId);
                const checked = childObjectId.every((child: any) => !(setting.derivates.includes(child)));
                return checked;
            }
        }
        return true;
    }

    public render() {
        let hiddenColumns = Object.keys(this.props.visibleColumns).filter(key => !this.props.visibleColumns[key].visible);
        let checkerColumns = hiddenColumns.length === 0 || (hiddenColumns.length === 1 && hiddenColumns.pop() === EColumnKey.DerivateKey);

        let hiddenDerivates: TDerivateColumnSettings[] | undefined;
        let checkerDerivates = false;
        if (this.props.derivatesList) {
            hiddenDerivates = this.props.visibleColumns && this.props.visibleColumns[EColumnKey.DerivateKey] ?
                this.props.visibleColumns[EColumnKey.DerivateKey].hiddenDerivates : undefined;
            const settingLoadedPSL = hiddenDerivates ? hiddenDerivates.find((item) => _.isEqual(item.pslSelection, this.props.loadedPSL)) : undefined;
            const loadedPSLHiddenDerivates = settingLoadedPSL ? settingLoadedPSL.derivates : [];

            // don't compare length because, if a derivate was deleted, but was set as hidden,
            // then it will remain in setting.derivateList until a reset to factory is done
            const derivateList = Array.from(_.cloneDeep(this.props.derivatesList).keys());
            let checkedDerivates = _.difference(derivateList, loadedPSLHiddenDerivates);
            checkerDerivates = _.isEqual(checkedDerivates.sort(), derivateList.sort());
        }

        // compare assignments filter fields
        const canCompareAssignments = this.props.availableDerivates !== undefined && this.props.availableDerivates.length > 1;
        const checkerCompareAssignments = canCompareAssignments &&
            // @ts-ignore already checked
            this.props.compareAssignmentsFilterValue !== undefined && this.props.compareAssignmentsFilterValue.size === this.props.availableDerivates.length;
        const compareAssignmentsFilterIsActive = this.props.compareAssignmentsFilterValue !== undefined && this.props.compareAssignmentsFilterValue.size > 0;
        return (
            <div className='filter-section'>
                <Dropdown
                    pointing="top left"
                    icon='table'
                    button={true}
                    scrolling={true}
                    className='icon'
                    style={{position: 'relative', display: 'inline-block'}}
                >
                    <Dropdown.Menu className="footer-dropdown-menu">

                        <Dropdown.Item
                            onClick={(e) => {
                                e.stopPropagation();
                                this.selectAllColumns(!checkerColumns);
                            }}
                        >
                            <Checkbox
                                checked={checkerColumns}
                                label={this.props.t("GLOBAL:SELECT_ALL")}
                                className={'disableClickEvents mr5'}
                            />
                            {this.props.isNewGridTable && this.state.isDerivateChanging && this.state.changingColumnKey === SELECT_ALL_KEY && (
                                <TableFooterLoader />
                            )}
                        </Dropdown.Item>

                        <Dropdown.Header
                            icon='tags'
                            content={this.props.t("GLOBAL:FILTER_COLUMNS")}
                        />
                        {Object.keys(this.props.visibleColumns).map(key =>
                            key !== EColumnKey.DerivateKey &&
                            <Dropdown.Item
                                key={"TableMenue" + key}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.onChange(key, !this.props.visibleColumns[key].visible);
                                }}
                            >
                                <Checkbox
                                    checked={this.props.visibleColumns[key].visible}
                                    className={'disableClickEvents mr5'}
                                    style={{marginRight: 5}}
                                />
                                {this.getLabel(key)}
                                {this.props.isNewGridTable && this.state.isDerivateChanging && this.state.changingColumnKey === key && (
                                    <TableFooterLoader />
                                )}
                            </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
                {this.props.loadedPSL && this.props.derivatesList && this.props.availableDerivates &&
                    <>
                        <Dropdown
                            icon={<Icon
                                name='car'
                                color={this.props.derivatesList.size === this.props.availableDerivates.length ? undefined : 'red'}
                            />}
                            pointing="top left"
                            button={true}
                            scrolling={true}
                            className='icon'
                        >
                            <Dropdown.Menu id='footer-actions-derivates'>
                                {(this.props.derivatesList.size > 0
                                    || this.props.availableDerivates.length > 0) &&
                                    <Dropdown.Item
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.selectAllDerivates(checkerDerivates);
                                        }}
                                    >
                                        <Checkbox
                                            checked={checkerDerivates}
                                            label={this.props.t("OPTIONS_VIEW:SELECT_ALL")}
                                            className={'disableClickEvents mr5'}
                                        />
                                        {this.props.isNewGridTable && this.state.isDerivateChanging && this.state.changingDerivateKey === SELECT_ALL_KEY && (
                                            <TableFooterLoader />
                                        )}
                                    </Dropdown.Item>
                                }

                                {this.props.generationsRows && this.props.generationsRows.length > 0 && (
                                    <>
                                        <Dropdown.Header
                                            icon='tags'
                                            onClick={(e: MouseEvent) => e.stopPropagation()}
                                            content={this.props.derivatesList.size > 0
                                                ? this.props.t("OPTIONS_VIEW:FILTER_BY_GENERATIONS")
                                                : this.props.t("OPTIONS_VIEW:EMPTY_DERIVATES_LIST")
                                            }
                                        />

                                        {this.props.generationsRows.map((generation: any, index: number) =>
                                            <Dropdown.Item
                                                key={generation.objectId}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (generation.child.length > 0) {
                                                        const derivates = generation.child.map((child: any) => child.objectId);
                                                        derivates.push(generation.objectId);
                                                        this.onChangeDerivate(EColumnKey.DerivateKey, derivates, `${generation.objectId}-${index}`);
                                                    } else {
                                                        this.onChangeDerivate(EColumnKey.DerivateKey, [generation.objectId], `${generation.objectId}-${index}`);
                                                    }
                                                }}
                                            >
                                                <Checkbox
                                                    className={'disableClickEvents mr5'}
                                                    checked={this.onCheckDerivate(generation)}
                                                />

                                                {generation.generation ? generation.generation.generation : ''}
                                                {this.props.isNewGridTable
                                                    && this.state.isDerivateChanging
                                                    && this.state.changingDerivateKey === `${generation.objectId}-${index}` && (
                                                        <TableFooterLoader />
                                                    )}
                                            </Dropdown.Item>
                                        )}
                                        <Dropdown.Divider />
                                    </>
                                )}
                                <Dropdown.Header
                                    icon='tags'
                                    onClick={(e: MouseEvent) => e.stopPropagation()}
                                    content={this.props.derivatesList.size > 0
                                        ? this.props.t("OPTIONS_VIEW:FILTER_DERIVATES")
                                        : this.props.t("OPTIONS_VIEW:EMPTY_DERIVATES_LIST")
                                    }
                                />
                                {Array.from(this.props.derivatesList.keys()).map((derivate, index) =>
                                    // @ts-ignore already checked
                                    this.props.availableDerivates.includes(derivate)
                                    &&
                                    <Dropdown.Item
                                        key={derivate}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.onChangeDerivate(EColumnKey.DerivateKey, [derivate], `${derivate}-${index}`);
                                        }}
                                    >
                                        <Checkbox
                                            className={'disableClickEvents mr5'}
                                            checked={
                                                !(hiddenDerivates && this.props.visibleColumns[EColumnKey.DerivateKey] &&
                                                    this.props.visibleColumns[EColumnKey.DerivateKey].hiddenDerivates !== undefined &&
                                                    // @ts-ignore
                                                    this.props.visibleColumns[EColumnKey.DerivateKey].hiddenDerivates.find((item) =>
                                                        _.isEqual(item.pslSelection, this.props.loadedPSL) &&
                                                        item.derivates.find(der => der === derivate)))
                                            }
                                        />
                                        {this.props.derivatesList && " " + this.props.derivatesList.get(derivate)}
                                        {this.props.isNewGridTable && this.state.isDerivateChanging && this.state.changingDerivateKey === `${derivate}-${index}` && (
                                            <TableFooterLoader />
                                        )}
                                    </Dropdown.Item>

                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                        {this.props.onHideEmptyAssignmentsRowsAndColumns &&
                            <Tooltip
                                className='spacial-height-tooltip'
                                tooltipMessage={this.props.hideEmptyAssignmentsRowsAndColumns
                                    ? this.props.t('OPTIONS_VIEW:SHOW_EMPTY_ROW_COLUMN')
                                    : this.props.t('OPTIONS_VIEW:HIDE_EMPTY_ROW_COLUMN')
                                }
                            >
                                <Button
                                    icon='th'
                                    active={this.props.hideEmptyAssignmentsRowsAndColumns}
                                    negative={true}
                                    size={'large'}
                                    style={{marginTop: '1px'}}
                                    className={`hide-derivate-button`}
                                    // @ts-ignore already checked
                                    onClick={this.props.onHideEmptyAssignmentsRowsAndColumns}
                                />
                            </Tooltip>
                        }
                        <Dropdown
                            icon='balance scale'
                            pointing="top left"
                            button={true}
                            scrolling={true}
                            className={compareAssignmentsFilterIsActive ? 'icon active' : 'icon'}
                            style={{width: 'auto'}}
                            floating={true}
                            labeled={compareAssignmentsFilterIsActive}
                            text={compareAssignmentsFilterIsActive ?
                                // @ts-ignore
                                this.props.compareAssignmentsFilterValue.size + ' ' + this.props.t("OPTIONS_VIEW:DERIVATE") : ''}
                        >
                            <Dropdown.Menu>
                                {canCompareAssignments &&
                                    <Dropdown.Item
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (this.props.onFilterByComparingAssignments) {
                                                const getAvalibleDerivates = this.props.derivatesList ? Array.from(this.props.derivatesList) : [];
                                                const getDerivateValues = getAvalibleDerivates
                                                    .filter((der) => this.props.availableDerivates && this.props.availableDerivates.includes(der[0])).map((der) => der[1]);
                                                this.props.onFilterByComparingAssignments(checkerCompareAssignments ?
                                                    new Set<string>() : new Set<string>(getDerivateValues));
                                            }
                                        }}
                                    >
                                        <Checkbox
                                            checked={checkerCompareAssignments}
                                            label={this.props.t("OPTIONS_VIEW:SELECT_ALL")}
                                            className={'disableClickEvents mr5'}
                                        />
                                    </Dropdown.Item>
                                }
                                <Dropdown.Header
                                    icon='tags'
                                    onClick={(e: MouseEvent) => e.stopPropagation()}
                                    className={canCompareAssignments ? '' : 'color--red'}
                                    content={canCompareAssignments
                                        ? this.props.t("OPTIONS_VIEW:COMPARE_ASSIGNMENTS")
                                        : this.props.t("OPTIONS_VIEW:EMPTY_COMPARE_LIST")
                                    }
                                />
                                {canCompareAssignments &&
                                    // @ts-ignore already checked in the variable above
                                    this.props.availableDerivates.map((derivate) => {
                                        const derivateName: any = this.props.derivatesList ? this.props.derivatesList.get(derivate) : '';
                                        return (
                                            <Dropdown.Item
                                                key={derivateName}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    this.onFilterByComparingAssignments(derivateName);
                                                }}
                                            >
                                                <Checkbox
                                                    className={'disableClickEvents mr5'}
                                                    checked={this.props.compareAssignmentsFilterValue
                                                        && this.props.compareAssignmentsFilterValue.has(derivateName)}
                                                />
                                                {" " + derivateName}
                                            </Dropdown.Item>
                                        );
                                    })}
                            </Dropdown.Menu>
                        </Dropdown>
                    </>
                }
                {this.props.onChangeViewMode && this.props.isFullViewMode !== undefined &&
                    <Tooltip
                        tooltipMessage={this.props.isFullViewMode
                            ? this.props.t('NORMALMODE')
                            : this.props.t('FULLVIEWMODE')
                        }
                    >
                        <Button
                            icon='eye'
                            active={this.props.isFullViewMode}
                            negative={true}
                            size={'large'}
                            className={`hide-derivate-button`}
                            // @ts-ignore already checked
                            onClick={this.props.onChangeViewMode}
                        />
                    </Tooltip>
                }
                {this.props.pdipFilterValue !== undefined && this.props.onFilterChange &&
                    <Dropdown
                        id={'table-pdip-filter'}
                        icon={'exchange'}
                        floating={true}
                        style={{width: "auto"}}
                        className={this.getPDiPFilterClass(this.props.pdipFilterValue)}
                        labeled={true}
                        button={true}
                        text={getPDiPFilterLabel(this.props.pdipFilterValue, true)}
                        // @ts-ignore
                        onChange={(e, v) => this.props.onFilterChange(PDiPFlag, v.value || "")}
                        value={this.props.pdipFilterValue}
                        renderLabel={this.renderPDiPFilterLabel}
                        options={this.getPDiPFilterDataSource()}
                    />
                }
                {this.props.pccdFilterValue !== undefined && this.props.onFilterChange &&
                    <Dropdown
                        id={'table-pccd-filter'}
                        icon={'stack exchange'}
                        floating={true}
                        style={{width: "auto"}}
                        className={this.getPCCDFilterClass(this.props.pccdFilterValue)}
                        labeled={true}
                        button={true}
                        text={getPCCDFilterLabel(this.props.pccdFilterValue, true)}
                        // @ts-ignore
                        onChange={(e, v) => this.props.onFilterChange(PCCDFlag, v.value || "")}
                        value={this.props.pccdFilterValue}
                        renderLabel={this.renderPCCDFilterLabel}
                        options={this.getPCCDFilterDataSource()}
                    />
                }
            </div >
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Table/components/footerComponents/TableFooterActions.tsx